import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Globe } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/i18n";

/**
 * Compact language picker. Two variants:
 *  - `variant="button"`  (default): pill button with globe + short code, opens a dropdown.
 *  - `variant="inline"`: a full-width list for use inside mobile menus / Settings.
 */
export default function LanguageSwitcher({ variant = "button" }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = i18n.resolvedLanguage || i18n.language || "en";
  const active = SUPPORTED_LANGUAGES.find((l) => l.code === current) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  if (variant === "inline") {
    return (
      <div className="space-y-1" data-testid="language-switcher-inline">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 pt-2 pb-1">
          {t("common.language")}
        </p>
        {SUPPORTED_LANGUAGES.map((l) => {
          const selected = l.code === current;
          return (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => change(l.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                selected ? "bg-[#FFF5E6] text-[#FF8A65] font-semibold" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className="text-base">{l.flag}</span>
              <span className="flex-1 text-left">{l.label}</span>
              {selected && <Check size={14} />}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="language-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
        aria-label={t("common.language")}
      >
        <Globe size={14} />
        <span>{active.code.toUpperCase()}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-11 w-44 bg-white rounded-2xl shadow-lg border border-slate-100 py-1.5 z-50 animate-fade-in"
          data-testid="language-switcher-menu"
        >
          {SUPPORTED_LANGUAGES.map((l) => {
            const selected = l.code === current;
            return (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => change(l.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                  selected ? "bg-[#FFF5E6] text-[#FF8A65] font-semibold" : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="text-base">{l.flag}</span>
                <span className="flex-1 text-left">{l.label}</span>
                {selected && <Check size={13} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
